/**
 * Modalità Focus: una sessione mirata avviata da "Riprendi da dove hai lasciato".
 * Salva obiettivo, durata e destinazione in localStorage così la sessione
 * sopravvive a cambi di rotta, refresh e chiusura dell'app (tempo reale).
 * La logica di calcolo è pura, così è testabile senza browser.
 */

export type FocusSession = {
  /** tipo di attività (quiz, cards, chat…) */
  kind: string;
  /** rotta dell'attività */
  to: string;
  /** titolo mostrato nella barra */
  label: string;
  /** obiettivo della sessione, in linguaggio naturale */
  goal: string;
  /** durata pianificata in minuti */
  minutes: number;
  /** epoch ms di avvio */
  startedAt: number;
  version: 1;
};

export type FocusProgress = {
  elapsedSec: number;
  remainingSec: number;
  /** 0..1 */
  ratio: number;
  done: boolean;
};

const STORAGE_KEY = "lumen.focus.v1";

/** Durata consigliata in base al tipo di attività da riprendere. */
export function suggestFocusMinutes(kind: string): number {
  switch (kind) {
    case "cards":
      return 10;
    case "quiz":
    case "chat":
      return 15;
    case "difficulty":
    case "map":
      return 20;
    default:
      return 25;
  }
}

export function focusProgress(session: FocusSession, now: Date | number = Date.now()): FocusProgress {
  const nowMs = typeof now === "number" ? now : now.getTime();
  const total = Math.max(1, Math.round(session.minutes * 60));
  const elapsedSec = Math.max(0, Math.floor((nowMs - session.startedAt) / 1000));
  const remainingSec = Math.max(0, total - elapsedSec);
  return {
    elapsedSec,
    remainingSec,
    ratio: Math.min(1, elapsedSec / total),
    done: remainingSec === 0,
  };
}

export function formatClock(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/* ------------------------------- storage --------------------------------- */

const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function subscribeFocus(cb: () => void): () => void {
  listeners.add(cb);
  if (typeof window !== "undefined") window.addEventListener("storage", cb);
  return () => {
    listeners.delete(cb);
    if (typeof window !== "undefined") window.removeEventListener("storage", cb);
  };
}

export function readFocus(): FocusSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<FocusSession>;
    if (!parsed || typeof parsed.startedAt !== "number" || typeof parsed.to !== "string") return null;
    return {
      kind: String(parsed.kind ?? "session"),
      to: parsed.to,
      label: String(parsed.label ?? ""),
      goal: String(parsed.goal ?? ""),
      minutes: typeof parsed.minutes === "number" ? parsed.minutes : 25,
      startedAt: parsed.startedAt,
      version: 1,
    };
  } catch {
    return null;
  }
}

export function startFocus(input: Omit<FocusSession, "startedAt" | "version"> & { startedAt?: number }): FocusSession {
  const session: FocusSession = {
    ...input,
    startedAt: input.startedAt ?? Date.now(),
    version: 1,
  };
  if (typeof window !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch {
      /* quota */
    }
  }
  emit();
  return session;
}

/** Termina la sessione e restituisce i minuti effettivamente fatti. */
export function endFocus(): number {
  const current = readFocus();
  if (typeof window !== "undefined") {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }
  emit();
  if (!current) return 0;
  return Math.round(focusProgress(current).elapsedSec / 60);
}
